// ---- Kopf-Menüs der Seitenköpfe (E1/E2) -------------------------------------
//
// Entstanden in Etappe 1 für den Posteingang (Mail.tsx trug acht Knöpfe
// nebeneinander), in Etappe 2 vom Kalender mitbenutzt: dort standen elf
// gleichrangige Elemente in der Werkzeugleiste. In beiden Fällen fällt NICHTS
// weg — die Zweitrangigen ziehen in ein „Ansicht"-Menü bzw. den ···-Überlauf.
//
// Hier liegt der Rahmen, weil es zwei Nutzer sind und die beiden nicht
// auseinanderlaufen dürfen: die Esc-Behandlung unten ist ein GEFIXTER BUG
// (siehe Kommentar), und ein kopierter Menü-Rahmen hätte den Fix nur an einer
// Stelle. Bauform bewusst wie `MailboxSwitcher`: relative + absolutes Panel +
// unsichtbarer Klick-Fänger statt Dokument-Listener. Kein Browser-Dialog, kein
// neues Popover-Framework.
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";

/** Gemeinsamer Rahmen beider Kopf-Menüs: Trigger + aufklappendes Panel. */
export function KopfMenu({
  triggerProps,
  trigger,
  panelClass,
  wrapperProps,
  children,
}: {
  triggerProps: React.ButtonHTMLAttributes<HTMLButtonElement> &
    Record<string, unknown>;
  trigger: React.ReactNode;
  panelClass?: string;
  wrapperProps?: React.HTMLAttributes<HTMLDivElement> & Record<string, unknown>;
  children: (close: () => void) => React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  return (
    <div
      className="relative"
      // Esc schließt das Menü. Ohne das blieb der unsichtbare Klick-Fänger über
      // der ganzen Seite liegen: das Menü sah zu aus, schluckte aber jeden
      // weiteren Klick. Ein Menü, das man nur per Mausklick daneben wieder
      // loswird, ist mit der Tastatur eine Sackgasse.
      onKeyDown={(e) => {
        if (e.key === "Escape" && open) {
          e.stopPropagation();
          setOpen(false);
        }
      }}
      {...wrapperProps}
    >
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        {...triggerProps}
      >
        {trigger}
      </button>
      {open && (
        <>
          {/* Klick außerhalb schließt (kein Doc-Listener). */}
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            onClick={close}
            className="fixed inset-0 z-10 cursor-default"
          />
          <div
            role="menu"
            className={clsx(
              "absolute right-0 z-20 mt-1 rounded-lg border border-border bg-surface py-1 shadow-lg",
              panelClass ?? "min-w-[15rem]"
            )}
          >
            {children(close)}
          </div>
        </>
      )}
    </div>
  );
}

/** Eine Zeile im Kopf-Menü: Symbol · Text · (optional) Tasten-Chip.
 *
 *  `to` macht daraus einen echten Link statt eines Knopfes. Der Kalender
 *  braucht das: sein Abstimmungs-Einstieg WAR ein `<Link>`, und ein Ziel, das
 *  man nicht mehr mit der mittleren Maustaste in einen neuen Tab legen kann,
 *  wäre eine still verlorene Funktion. `badge` trägt die Zahl daneben mit um. */
export function KopfMenuItem({
  icon,
  label,
  taste,
  badge,
  to,
  onClick,
  disabled,
  ...rest
}: {
  icon: React.ReactNode;
  label: string;
  taste?: string;
  badge?: React.ReactNode;
  to?: string;
  onClick: () => void;
  disabled?: boolean;
} & Record<string, unknown>) {
  const klasse =
    "flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-xs text-text-2 transition-colors hover:bg-surface-2 hover:text-text disabled:opacity-50";
  const innen = (
    <>
      <span className="shrink-0 text-text-3">{icon}</span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {badge}
      {taste && (
        <kbd className="shrink-0 rounded border border-border bg-surface-2 px-1.5 py-px text-[10px] font-normal text-text-3">
          {taste}
        </kbd>
      )}
    </>
  );
  if (to)
    return (
      <Link to={to} role="menuitem" onClick={onClick} className={klasse} {...rest}>
        {innen}
      </Link>
    );
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={klasse}
      {...rest}
    >
      {innen}
    </button>
  );
}
